import React, { useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import BdrModal from "../BdrModal/BdrModal";

/**
 * BdrButton — every button in the admin app.
 *
 * Four intents sharing one shape, so the same action looks the same
 * everywhere:
 *
 *   default  neutral / cancel / secondary
 *   action   go somewhere, change the view, open a panel — blue, and
 *            deliberately NOT green, so navigation stops competing with the
 *            button that actually saves
 *   confirm  create, save, approve — anything additive
 *   danger   delete, remove — anything destructive
 *
 * `action` and `confirm` each have a solid weight (`action-solid`,
 * `confirm-solid`) picked by the SURFACE, not by importance: solid on the
 * saturated --bdr-grad-panel, subdued on the neutral container panels.
 *
 * The classes live in index.css; this component exists for the three
 * behaviors that otherwise get reimplemented per call site:
 *
 *   1. DESTRUCTIVE CONFIRM. A `danger` button asks before it fires, using the
 *      modal shell. The confirm lives here so every delete in the app phrases
 *      and stacks the same way, instead of each page inventing a dialog (or
 *      skipping one).
 *   2. COLLAPSE TO ICON. Pass `icon` + `collapseToIcon` and the label drops on
 *      a phone, keeping the glyph and an accessible name. This is the pattern
 *      already used by hand across the app; centralising it means nobody
 *      forgets the aria-label half.
 *   3. THE LADDER. The confirm opens at `raised` by default because a delete
 *      button almost always lives inside a modal already — a confirm that
 *      renders behind the modal that raised it is useless. Inside an already
 *      raised modal, pass confirmLayer="top".
 *
 * <BdrButton variant="danger" note="This removes the note permanently."
 *            onClick={remove}>Delete</BdrButton>
 */
const BdrButton = ({
  children,
  onClick,
  /** "default" (outline) | "confirm" (accent fill) | "danger" | "plain" (no chrome) */
  variant = "default",
  /** "md" (default) | "sm" — sm for dense rows and secondary controls */
  size = "md",
  /** variant of the button that performs the action in the confirm dialog.
      Defaults to danger: a confirm gate means the action is worth pausing for. */
  confirmVariant = "danger",
  /** MUI icon element, e.g. <DeleteIcon /> */
  icon,
  /** drop the label and show only the icon at <=600px */
  collapseToIcon = false,
  /** always icon-only, at every width */
  iconOnly = false,
  /** ask before firing. Defaults ON for `danger`, OFF otherwise. */
  confirm,
  /** the explanation shown in the confirm — say what is irreversible */
  note,
  confirmTitle,
  confirmLabel,
  /** "base" | "raised" (default) | "top" — see the overlay ladder in :root */
  confirmLayer = "raised",
  disabled = false,
  type = "button",
  className = "",
  ariaLabel,
  title,
  ...rest
}) => {
  const smaller600 = useMediaQuery("(max-width:600px)");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const needsConfirm = confirm === undefined ? variant === "danger" : !!confirm;
  const showIconOnly = iconOnly || (collapseToIcon && smaller600 && !!icon);

  // An icon-only button has no visible text, so it MUST carry a name.
  const accessibleName =
    ariaLabel || (typeof children === "string" ? children : undefined);

  const classes = [
    "bdr-btn",
    `bdr-btn--${variant}`,
    size === "sm" ? "bdr-btn--sm" : "",
    showIconOnly ? "bdr-btn--icon" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const handleClick = (event) => {
    if (needsConfirm) {
      event.preventDefault();
      setConfirmOpen(true);
      return;
    }
    onClick?.(event);
  };

  const runConfirmed = () => {
    setConfirmOpen(false);
    onClick?.();
  };

  return (
    <>
      <button
        type={type}
        className={classes}
        onClick={handleClick}
        disabled={disabled}
        aria-label={showIconOnly ? accessibleName : ariaLabel}
        title={title || (showIconOnly ? accessibleName : undefined)}
        {...rest}
      >
        {icon}
        {!showIconOnly && children}
      </button>

      {needsConfirm && (
        <BdrModal
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          title={confirmTitle || "Are you sure?"}
          size="sm"
          /* `panel` is the app's standard modal surface — 48 of the call
             sites pass it. This one did not, so EVERY confirm dialog in the
             app (they all come from here) rendered on the bare default and
             was the one modal shape that did not match the others. */
          variant="panel"
          layer={confirmLayer}
          actions={
            <>
              <BdrButton onClick={() => setConfirmOpen(false)}>
                Cancel
              </BdrButton>
              {/* confirm={false} breaks the recursion — this is the button
                  that actually performs the action. */}
              <BdrButton
                variant={confirmVariant}
                confirm={false}
                onClick={runConfirmed}
              >
                {confirmLabel ||
                  (typeof children === "string" ? children : "Confirm")}
              </BdrButton>
            </>
          }
        >
          <p className="bdr-confirm-note">
            {note || "This can't be undone."}
          </p>
        </BdrModal>
      )}
    </>
  );
};

export default BdrButton;
